import React, { useCallback } from "react";
import { BoxesIcon, FileTextIcon } from "lucide-react";
import { observer } from "mobx-react-lite";

import {
  AVAILABLE_PROVIDER_SELECTIONS,
  PROVIDER_SELECTION_LABELS,
  getProviderSelectionLogoProvider,
  providerSelectionToPreset,
  VENDOR_AGNOSTIC_PROVIDER,
  type SkillCollectionProviderSelection,
} from "../skill-presets";
import { SkillCollectionEmptySelectionCard } from "../SkillCollectionEmptySelectionCard";
import { SkillCollectionSplitPane } from "../SkillCollectionSplitPane";
import { useSkillCollectionEditorStore } from "../store";

import { EditorSidebar } from "./EditorSidebar";
import { FileEditor } from "./FileEditor";

import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface EditorLayoutProps {
  versionFiles: Record<string, string> | null;
  isLoadingVersionFiles?: boolean;
  onProviderChange: (preset: SkillCollectionProviderSelection) => void;
  provider: SkillCollectionProviderSelection;
}

export const EditorLayout = observer(
  ({ versionFiles, isLoadingVersionFiles, onProviderChange, provider }: EditorLayoutProps) => {
    const store = useSkillCollectionEditorStore();
    const { dirtyFilePaths, selectedPath } = store;
    const presetProvider = providerSelectionToPreset(provider);
    const isVendorAgnostic = provider === VENDOR_AGNOSTIC_PROVIDER;

    const handleInitialLoad = useCallback(
      (content: string) => {
        if (!selectedPath) {
          return;
        }
        store.loadInitialContent(selectedPath, content);
      },
      [selectedPath, store],
    );

    return (
      <SkillCollectionSplitPane
        sidebar={
          <EditorSidebar
            tree={store.displayTree}
            dirtyFilePaths={dirtyFilePaths}
            selectedPath={selectedPath}
            onSelectFile={store.setSelectedPath}
            onMoveNode={store.moveNode}
            onDeleteNode={store.deleteNode}
            onCreateFile={store.createFile}
            onCreateFolder={store.createFolder}
            getCreateError={store.getCreateError}
            getRenameError={store.getRenameError}
            onRenameNode={store.renameNode}
            provider={provider}
            onProviderChange={onProviderChange}
          />
        }
      >
        {selectedPath ? (
          <FileEditor
            key={selectedPath}
            relativePath={selectedPath}
            editedContent={store.contentByPath.get(selectedPath)}
            documentJson={store.getDocumentJson(selectedPath)}
            isDirty={dirtyFilePaths.has(selectedPath)}
            isDraftFile={!store.serverFilePaths.has(selectedPath)}
            hasImportedContent={store.hasImportedContent}
            onClearImportFlag={store.clearImportFlag}
            versionFileContent={versionFiles?.[selectedPath]}
            isLoadingVersionFiles={isLoadingVersionFiles}
            provider={presetProvider}
            onContentChange={content => store.updateContent(selectedPath, content)}
            onDocumentJsonChange={store.updateDocumentJson}
            onEditorReady={doc => store.snapshotEditorBaseline(selectedPath, doc)}
            onInitialLoad={handleInitialLoad}
          />
        ) : (
          <>
            {store.isEmpty ? (
              <SkillCollectionEmptySelectionCard
                icon={
                  isVendorAgnostic ? (
                    <BoxesIcon className="h-6 w-6" />
                  ) : (
                    <FileTextIcon className="h-6 w-6" />
                  )
                }
                title="Start this skill collection"
                description={
                  isVendorAgnostic
                    ? "Create or import files directly for a portable, vendor-agnostic skill layout, or switch to a provider-specific scaffold."
                    : "Choose a provider to generate a starter structure, then tailor the files to your workflow."
                }
                action={
                  <div className="mx-auto w-[320px] max-w-full">
                    <Select value={provider} onValueChange={onProviderChange}>
                      <SelectTrigger className="h-11 w-full">
                        <SelectValue placeholder="Choose a layout..." />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_PROVIDER_SELECTIONS.map(option => (
                          <SelectItem key={option} value={option}>
                            <div className="flex items-center gap-2">
                              {option === VENDOR_AGNOSTIC_PROVIDER ? (
                                <BoxesIcon className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ProviderWithLogo
                                  provider={getProviderSelectionLogoProvider(option) ?? ""}
                                  className="!mr-0"
                                  hideName
                                />
                              )}
                              <span>{PROVIDER_SELECTION_LABELS[option]}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                }
              />
            ) : (
              <SkillCollectionEmptySelectionCard
                icon={<FileTextIcon className="h-6 w-6" />}
                title="Select a file to start editing"
                description="Choose a file from the sidebar, or create a new file or folder to continue building this collection."
              />
            )}
          </>
        )}
      </SkillCollectionSplitPane>
    );
  },
);
